import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { COMPETITION_DISCIPLINES, competitionBoards } from '@structural-climbing/validation'
import { useGymDisciplines } from '../../../layouts/useGymDisciplines'
import type { Competition, CompetitionFormat, CompetitionScoringType } from '../types'

/**
 * How a competitor gets from "joined" to "may log tops".
 *
 * Stored as `requires_confirmation` today: `free` is false, `desk` is true.
 * `online` arrives with card payments and is not selectable yet.
 */
export type EntryMode = 'free' | 'desk' | 'online'

/** The three steps this form covers. The rest of setup has its own screens. */
export type FormStep = 'about' | 'when' | 'entry'

/** Which step owns each payload field, so a Worker rejection lands on the right screen. */
const FIELD_STEP: Record<string, FormStep> = {
  name: 'about',
  description: 'about',
  prizes: 'about',
  poster_url: 'about',
  discipline: 'when',
  format: 'when',
  scoring_type: 'when',
  starts_at: 'when',
  ends_at: 'when',
  join_policy: 'entry',
  entry_fee_cents: 'entry',
  entry_fee_currency: 'entry',
  requires_confirmation: 'entry',
}

/** Trims a wall-clock value to the minute-precision value a DateField accepts. */
function toInputValue(value: string | undefined): string {
  return value ? value.slice(0, 16) : ''
}

export function entryModeOf(c: Pick<Competition, 'requires_confirmation'> | null): EntryMode {
  return c?.requires_confirmation ? 'desk' : 'free'
}

/**
 * The fields of a competition that does not exist yet, as kept in the browser
 * between visits. Steps 1 and 2 save nothing to the server, so a refresh would
 * otherwise throw away everything typed so far.
 */
export interface FormDraft {
  name: string
  description: string
  prizes: string
  posterUrl: string | null
  discipline: string
  format: CompetitionFormat
  scoringType: CompetitionScoringType
  startsAt: string
  endsAt: string
  joinPolicy: 'open' | 'members_only'
  entryMode: EntryMode
  entryFee: string
  entryFeeCurrency: string
}

export type SaveResult = { ok: true; id: string } | { ok: false; step: FormStep | null }

/**
 * The fields of steps 1–3 (about, when and how, entry), shared by the new
 * competition page and the setup steps of an existing one.
 *
 * Moved out of the old create/edit modal unchanged in what it validates and
 * sends: the Worker contract is the same, only the screens around it moved.
 */
export function useCompetitionForm(editTarget: Competition | null, draft?: FormDraft | null) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // A bouldering-only gym cannot run a lead or a combined competition. One that
  // already exists keeps its own discipline in the list, so it stays editable.
  const gymDisciplines = useGymDisciplines()
  const disciplineOptions = COMPETITION_DISCIPLINES.filter(
    (d) =>
      d === editTarget?.discipline ||
      competitionBoards(d).every((board) => gymDisciplines.includes(board)),
  )

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [prizes, setPrizes] = useState('')
  const [posterUrl, setPosterUrl] = useState<string | null>(null)
  const [discipline, setDiscipline] = useState<(typeof COMPETITION_DISCIPLINES)[number]>(
    gymDisciplines[0],
  )
  const [format, setFormat] = useState<CompetitionFormat>('single_day')
  const [scoringType, setScoringType] = useState<CompetitionScoringType>('thousand_split')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [joinPolicy, setJoinPolicy] = useState<'open' | 'members_only'>('members_only')
  const [entryMode, setEntryMode] = useState<EntryMode>('free')
  // Kept as strings: an empty field means no fee, which a number cannot express.
  const [entryFee, setEntryFee] = useState('')
  const [entryFeeCurrency, setEntryFeeCurrency] = useState('RON')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Validation messages keyed by payload field, from this form and from the
   * Worker alike, so a rejected field says so on its own control.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Reloads when the competition is re-read, so a save elsewhere shows here.
  // A new competition starts from the browser's draft, when there is one; it
  // is read once, so later renders never put back what was since changed.
  const [startDraft] = useState(() => (editTarget ? null : (draft ?? null)))
  useEffect(() => {
    const d = editTarget ? null : startDraft
    setName(editTarget?.name ?? d?.name ?? '')
    setDescription(editTarget?.description ?? d?.description ?? '')
    setPrizes(editTarget?.prizes ?? d?.prizes ?? '')
    setPosterUrl(editTarget?.poster_url ?? d?.posterUrl ?? null)
    // A drafted discipline the gym no longer offers falls back to its first.
    const drafted = disciplineOptions.find((x) => x === d?.discipline)
    setDiscipline(editTarget?.discipline ?? drafted ?? gymDisciplines[0])
    setFormat(editTarget?.format ?? d?.format ?? 'single_day')
    setScoringType(editTarget?.scoring_type ?? d?.scoringType ?? 'thousand_split')
    setStartsAt(editTarget ? toInputValue(editTarget.starts_at) : (d?.startsAt ?? ''))
    setEndsAt(editTarget ? toInputValue(editTarget.ends_at) : (d?.endsAt ?? ''))
    setJoinPolicy(editTarget?.join_policy ?? d?.joinPolicy ?? 'members_only')
    setEntryMode(editTarget ? entryModeOf(editTarget) : (d?.entryMode ?? 'free'))
    // A fee of 0 is a real value some competitions carry; only null is "none".
    setEntryFee(
      editTarget
        ? editTarget.entry_fee_cents != null
          ? String(editTarget.entry_fee_cents / 100)
          : ''
        : (d?.entryFee ?? ''),
    )
    setEntryFeeCurrency(editTarget?.entry_fee_currency ?? d?.entryFeeCurrency ?? 'RON')
    setError(null)
    setFieldErrors({})
    // disciplineOptions is derived from these two on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTarget, gymDisciplines, startDraft])

  const snapshot: FormDraft = {
    name,
    description,
    prizes,
    posterUrl,
    discipline,
    format,
    scoringType,
    startsAt,
    endsAt,
    joinPolicy,
    entryMode,
    entryFee,
    entryFeeCurrency,
  }

  /** Puts one message on one control and clears the generic banner. */
  function failField(field: string, message: string): FormStep {
    setFieldErrors({ [field]: message })
    setError(null)
    return FIELD_STEP[field] ?? 'about'
  }

  /** Checks one step's fields. Returns the step to show an error on, or null. */
  const validate = useCallback(
    (step: FormStep): FormStep | null => {
      if (step === 'about') {
        if (!name.trim()) return failField('name', t('competitions.errorNameRequired'))
      }
      if (step === 'when') {
        if (!startsAt || !endsAt)
          return failField(startsAt ? 'ends_at' : 'starts_at', t('competitions.errorDatesRequired'))
        if (endsAt <= startsAt) return failField('ends_at', t('competitions.errorEndBeforeStart'))
        // The format is what the member page and the time-slot groups are built
        // around, so a "single day" spanning a week is not a harmless mismatch.
        if (format === 'single_day' && startsAt.slice(0, 10) !== endsAt.slice(0, 10))
          return failField('ends_at', t('competitions.errorSingleDaySpansDays'))
      }
      if (step === 'entry') {
        // A price with no currency is unreadable, so the pair moves together.
        if (entryFee && entryFeeCurrency.trim().length !== 3)
          return failField('entry_fee_currency', t('competitions.errorFeeNeedsCurrency'))
      }
      setFieldErrors({})
      return null
    },
    [name, startsAt, endsAt, format, entryFee, entryFeeCurrency, t],
  )

  /**
   * Saves steps 1–3. A new competition is created as a draft; an existing one
   * is patched with every field these steps own, never `published`.
   */
  async function save(): Promise<SaveResult> {
    for (const step of ['about', 'when', 'entry'] as const) {
      const failed = validate(step)
      if (failed) return { ok: false, step: failed }
    }
    setSaving(true)
    setError(null)
    setFieldErrors({})
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        editTarget ? `${apiUrl}/competitions/${editTarget.id}` : `${apiUrl}/competitions`,
        {
          method: editTarget ? 'PATCH' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            prizes: prizes.trim() || null,
            discipline,
            format,
            scoring_type: scoringType,
            starts_at: startsAt,
            ends_at: endsAt,
            // A new competition starts hidden; publishing is the last step.
            ...(editTarget ? {} : { published: false }),
            join_policy: joinPolicy,
            // Sent in minor units, so 13 becomes 1300 and no float survives.
            entry_fee_cents: entryFee ? Math.round(Number(entryFee) * 100) : null,
            entry_fee_currency: entryFee ? entryFeeCurrency : null,
            requires_confirmation: entryMode !== 'free',
            poster_url: posterUrl,
          }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          code?: string
          error?: string
          field?: string
          groups?: string[]
          rounds?: string[]
          routes?: string[]
        }
        // Moving the dates would strand a time-slot group or a round outside the
        // window. Name them, so it is clear what has to be fixed first.
        if (body.code === 'SESSIONS_OUTSIDE_WINDOW') {
          setError(
            t('competitions.errorSessionsOutsideWindow', {
              groups: (body.groups ?? []).join(', '),
            }),
          )
          return { ok: false, step: 'when' }
        }
        if (body.code === 'ROUNDS_OUTSIDE_WINDOW') {
          setError(
            t('competitions.errorRoundsOutsideWindow', { rounds: (body.rounds ?? []).join(', ') }),
          )
          return { ok: false, step: 'when' }
        }
        // Narrowing the discipline would leave routes no board scores.
        if (body.code === 'ROUTES_OTHER_DISCIPLINE')
          return {
            ok: false,
            step: failField(
              'discipline',
              t('competitions.errorRoutesOtherDiscipline', {
                routes: (body.routes ?? []).join(', '),
              }),
            ),
          }
        // Every validation 400 from the Worker names the field that failed.
        // Prefer our own translated wording, and fall back to the Worker's
        // English so an unmapped field still says something specific.
        if (body.field) {
          const key = `competitions.errorField_${body.field}`
          return {
            ok: false,
            step: failField(body.field, i18n.exists(key) ? t(key) : (body.error ?? '')),
          }
        }
        setError(t('competitions.errorSaveFailed'))
        return { ok: false, step: null }
      }
      const saved = (await res.json().catch(() => ({}))) as { id?: string }
      return { ok: true, id: saved.id ?? editTarget?.id ?? '' }
    } catch {
      setError(t('competitions.errorSaveFailed'))
      return { ok: false, step: null }
    } finally {
      setSaving(false)
    }
  }

  return {
    isNew: editTarget === null,
    snapshot,
    disciplineOptions,
    name,
    setName,
    description,
    setDescription,
    prizes,
    setPrizes,
    posterUrl,
    setPosterUrl,
    discipline,
    setDiscipline,
    format,
    setFormat,
    scoringType,
    setScoringType,
    startsAt,
    setStartsAt,
    endsAt,
    setEndsAt,
    joinPolicy,
    setJoinPolicy,
    entryMode,
    setEntryMode,
    entryFee,
    setEntryFee,
    entryFeeCurrency,
    setEntryFeeCurrency,
    saving,
    error,
    setError,
    fieldErrors,
    validate,
    save,
  }
}

export type CompetitionForm = ReturnType<typeof useCompetitionForm>
